import axios from 'axios'

/**
 * What a failed password-reset submission actually means.
 *
 * It exists because the view used to collapse every non-policy failure into
 * "your invitation has expired" (A9, 2026-09-18): a dropped connection, a
 * timeout, a 500 and a genuinely dead token all rendered the same terminal
 * message. For a client setting their password for the first time that is an
 * onboarding dead end, and the true remedy - try again - is exactly what the
 * message tells them is pointless.
 *
 * `bad-token` is returned ONLY on a definite verdict from the server. Anything
 * where we never got a verdict is `unreachable`, which keeps the form up. That
 * asymmetry is deliberate: telling someone to retry a genuinely dead link costs
 * one click, while telling someone their link is dead when it was a network
 * blip ends their onboarding.
 */
export type ResetErrorKind = 'policy' | 'bad-token' | 'unreachable'

/** The reset endpoint answers a refused PASSWORD with a structured detail. */
export function policyReasonFrom(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (detail && typeof detail === 'object') {
    const { code, reason } = detail as { code?: string; reason?: string }
    if (code === 'RESET_PASSWORD_INVALID_PASSWORD' && reason) return reason
  }
  return null
}

export function classifyResetError(error: unknown): ResetErrorKind {
  if (policyReasonFrom(error)) return 'policy'

  // A bare string detail is how the endpoint rejects the TOKEN itself
  // (asserted in tests/api/test_reset_password_policy_endpoint.py).
  if (
    axios.isAxiosError(error) &&
    error.response?.status === 400 &&
    error.response.data?.detail === 'RESET_PASSWORD_BAD_TOKEN'
  ) {
    return 'bad-token'
  }

  return 'unreachable'
}
